import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import {
  X, Edit3, CheckCircle2, Trash2, Clock, MapPin, User as UserIcon,
  Building2, Users as UsersIcon, AlertTriangle, Bell, FileText,
  Calendar as CalIcon, PhoneCall, CalendarClock, RotateCcw, ArrowRight,
} from "lucide-react";
import { cn, dateLocale } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import api from "@/lib/api";
import { getMeta, PRIORITIES, STATUSES } from "./calendarTypes";
import { formatHM } from "./calendarHelpers";
import { translateFr as tfr } from "@/i18n";

const fmtDate = (iso) => {
  if (!iso) return "";
  const s = new Date(iso).toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtDateShort = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
};
const initials = (name) => (name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const PRIORITY_BADGE = {
  low: "border-border text-muted-foreground bg-secondary/50",
  high: "border-amber-500/40 text-amber-700 bg-amber-500/10 dark:text-amber-300",
  urgent: "border-red-500/40 text-red-700 bg-red-500/10 dark:text-red-300",
};
const STATUS_DOT = { planned: "bg-blue-500", done: "bg-emerald-500", cancelled: "bg-zinc-400" };

/**
 * EventDetailSheet — view + quick-action drawer for a calendar event.
 * Desktop: right drawer (440px). Mobile: bottom sheet. Theme-token based.
 */
export default function EventDetailSheet({ open, onOpenChange, event, onEdit, onChanged, onDeleted }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [player, setPlayer] = useState(null);
  const [club, setClub] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open || !event) return;
    setConfirmDelete(false);
    setPlayer(null);
    setClub(null);
    if (event.player_id) {
      api.get(`/players/${event.player_id}`).then((r) => setPlayer(r.data.player || null)).catch(() => {});
    }
    if (event.club_id) {
      api.get("/clubs").then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.clubs || [];
        setClub(list.find((c) => c.id === event.club_id) || null);
      }).catch(() => {});
    }
  }, [open, event]);

  if (!event) return null;

  const meta = getMeta(event.type);
  const Icon = meta.Icon;
  const status = STATUSES.find((s) => s.value === event.status) || STATUSES[0];
  const priority = PRIORITIES.find((p) => p.value === event.priority) || PRIORITIES[1];
  const isDone = event.status === "done";
  const isCancelled = event.status === "cancelled";
  const sameDay = !event.ends_at || fmtDate(event.starts_at) === fmtDate(event.ends_at);
  const timeLabel = event.all_day
    ? tfr("Toute la journée")
    : `${formatHM(event.starts_at)}${event.ends_at ? ` – ${formatHM(event.ends_at)}` : ""}`;
  const participants = (event.participants || "").split(",").map((s) => s.trim()).filter(Boolean);

  const patch = async (updates, successMsg) => {
    setBusy(true);
    try {
      const r = await api.patch(`/events/${event.id}`, updates);
      toast.success(successMsg || tfr("Mis à jour"));
      onChanged?.(r.data);
      onOpenChange(false);
    } catch {
      toast.error(tfr("Impossible de mettre à jour l'événement."));
    } finally {
      setBusy(false);
    }
  };

  const markDone = () => patch({ status: "done" }, tfr("Événement marqué comme terminé."));
  const reopen = () => patch({ status: "planned" }, tfr("Événement réouvert."));
  const reschedule = () => { onEdit?.(event); onOpenChange(false); };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/events/${event.id}`);
      toast.success(tfr("Événement supprimé"));
      onDeleted?.(event.id);
      onOpenChange(false);
    } catch {
      toast.error(tfr("Impossible de supprimer l'événement."));
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const goPlayer = () => {
    onOpenChange(false);
    setTimeout(() => navigate(`/players/${event.player_id}`), 150);
  };
  const goClubs = () => {
    onOpenChange(false);
    setTimeout(() => navigate(`/clubs`), 150);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          data-testid="event-detail"
          aria-describedby={undefined}
          className={cn(
            "fixed z-50 bg-card text-card-foreground border border-border shadow-xl flex flex-col overflow-hidden",
            "left-0 right-0 bottom-0 h-[88vh] max-h-[88vh] rounded-t-3xl",
            "md:left-auto md:right-0 md:top-0 md:bottom-0 md:translate-x-0 md:translate-y-0",
            "md:h-screen md:max-h-screen md:w-[460px] md:rounded-l-2xl md:rounded-tr-none",
            "duration-300 ease-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "md:data-[state=closed]:slide-out-to-right md:data-[state=open]:slide-in-from-right"
          )}
        >
          {/* Mobile handle */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          {/* Header */}
          <header className="px-5 md:px-6 pt-3 md:pt-5 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 text-foreground text-xs font-medium px-2.5 py-1" data-testid="event-detail-category">
                <span className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} aria-hidden />
                <Icon className="h-3 w-3 text-muted-foreground" aria-hidden />
                {meta.label}
              </span>
              <DialogPrimitive.Close
                className="rounded-full h-9 w-9 flex items-center justify-center border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
                aria-label={tfr("Fermer")}
                data-testid="event-detail-close"
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
            <DialogPrimitive.Title className="text-xl md:text-2xl font-semibold tracking-tight leading-tight text-foreground" data-testid="event-detail-title">
              {event.title}
            </DialogPrimitive.Title>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12px] text-muted-foreground">
              <span data-testid="event-detail-date">{fmtDate(event.starts_at)}</span>
              {!event.all_day && (
                <><span aria-hidden>·</span><span className="tabular-nums">{timeLabel}</span></>
              )}
              {event.all_day && (<><span aria-hidden>·</span><span>{tfr("Toute la journée")}</span></>)}
              {priority.value !== "normal" && (
                <span className={cn("inline-flex items-center gap-1 rounded-full border text-[11px] font-medium px-2 py-0.5", PRIORITY_BADGE[priority.value])} data-testid="event-detail-priority">
                  {(priority.value === "high" || priority.value === "urgent") && <AlertTriangle className="h-3 w-3" aria-hidden />}
                  Priorité {priority.label.toLowerCase()}
                </span>
              )}
            </div>
          </header>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-5 md:px-6 py-5 space-y-4">
            <Field icon={Clock} label="Quand" testid="event-detail-when" value={
              <>
                <span className="block">{fmtDate(event.starts_at)}{!sameDay ? ` → ${fmtDate(event.ends_at)}` : ""}</span>
                <span className="block text-muted-foreground tabular-nums">{timeLabel}</span>
              </>
            } />
            {event.location && <Field icon={MapPin} label="Lieu" value={event.location} testid="event-detail-location" />}

            {event.player_id && (
              <EntityCard
                label={tfr("Athlète lié")}
                onClick={goPlayer}
                cta={tfr("Ouvrir la fiche")}
                testid="event-detail-player"
                avatar={
                  <Avatar className="h-9 w-9">
                    {player?.photo_url && <AvatarImage src={player.photo_url} alt="" />}
                    <AvatarFallback className="text-xs font-semibold bg-secondary">{initials(player?.name || event.player?.name || "?")}</AvatarFallback>
                  </Avatar>
                }
                title={player?.name || event.player?.name || "Chargement…"}
                subtitle={[player?.sport, player?.club].filter(Boolean).join(" · ")}
              />
            )}
            {event.club_id && (
              <EntityCard
                label={tfr("Club lié")}
                onClick={goClubs}
                cta="Ouvrir"
                testid="event-detail-club"
                avatar={
                  <span className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                  </span>
                }
                title={club?.name || "Chargement…"}
                subtitle={club?.competition || ""}
              />
            )}

            {participants.length > 0 && (
              <div className="flex items-start gap-3" data-testid="event-detail-participants">
                <UsersIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Participants</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {participants.map((p) => (
                      <span key={p} className="rounded-full bg-secondary text-foreground text-xs font-medium px-2.5 py-1">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <Field icon={Bell} label="Rappel" value={readableReminder(event.reminder)} testid="event-detail-reminder" />
            <Field icon={CalIcon} label={tfr("Statut")} testid="event-detail-status" value={
              <span className="inline-flex items-center gap-1.5">
                <span className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[status.value] || "bg-blue-500")} aria-hidden />
                {status.label}
              </span>
            } />

            {(event.description || event.notes) && (
              <div data-testid="event-detail-description">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <FileText className="h-3 w-3" aria-hidden /> Description
                </div>
                <p className="text-[13px] text-foreground/90 whitespace-pre-wrap leading-relaxed">
                  {event.description || event.notes}
                </p>
              </div>
            )}

            {(event.created_at || event.updated_at) && (
              <p className="text-[11px] text-muted-foreground/70 pt-1" data-testid="event-detail-meta">
                {event.created_at && <>Créé le {fmtDateShort(event.created_at)}</>}
                {event.created_at && event.updated_at && <span aria-hidden> · </span>}
                {event.updated_at && <>Modifié le {fmtDateShort(event.updated_at)}</>}
              </p>
            )}
          </div>

          {/* Footer actions */}
          <footer className="border-t border-border bg-card px-5 md:px-6 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] md:pb-3 space-y-2">
            {isDone || isCancelled ? (
              <Button
                variant="outline"
                onClick={reopen}
                disabled={busy}
                className="w-full h-11"
                data-testid="event-detail-reopen"
              >
                <RotateCcw className="h-4 w-4 mr-2" aria-hidden /> Réouvrir
              </Button>
            ) : (
              <Button
                onClick={markDone}
                disabled={busy}
                className="w-full h-11 bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="event-detail-done"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" aria-hidden /> Marquer comme terminé
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => { onEdit?.(event); onOpenChange(false); }}
                disabled={busy}
                className="h-10"
                data-testid="event-detail-edit"
              >
                <Edit3 className="h-4 w-4 mr-2" aria-hidden /> Modifier
              </Button>
              <Button
                variant="outline"
                onClick={reschedule}
                disabled={busy}
                className="h-10"
                data-testid="event-detail-postpone"
              >
                <CalendarClock className="h-4 w-4 mr-2" aria-hidden /> Reprogrammer
              </Button>
            </div>
            {(event.type === "relance" || event.type === "follow-up" || event.type === "call") && event.player_id && (
              <Button
                variant="outline"
                onClick={goPlayer}
                className="w-full h-10"
                data-testid="event-detail-call"
              >
                <PhoneCall className="h-4 w-4 mr-2" aria-hidden /> Ouvrir l'athlète
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="w-full h-9 text-red-600 hover:text-red-700 hover:bg-red-500/10 dark:text-red-400 dark:hover:text-red-300 text-xs"
              data-testid="event-detail-delete"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" aria-hidden /> Supprimer l'événement
            </Button>
          </footer>

          {/* Delete confirmation */}
          {confirmDelete && (
            <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[2px] flex items-center justify-center p-6" role="alertdialog" aria-label={tfr("Supprimer cet événement ?")} data-testid="event-detail-delete-confirm">
              <div className="rounded-2xl border border-border bg-card shadow-xl p-5 w-full max-w-sm">
                <p className="text-sm font-semibold">{tfr("Supprimer cet événement ?")}</p>
                <p className="text-xs text-muted-foreground mt-1">{tfr("Cette action est définitive.")}</p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" variant="ghost" size="sm" className="h-10" onClick={() => setConfirmDelete(false)} data-testid="event-detail-delete-cancel">
                    {tfr("Annuler")}
                  </Button>
                  <Button type="button" size="sm" disabled={busy} className="h-10 bg-red-600 text-white hover:bg-red-700"
                    onClick={remove} data-testid="event-detail-delete-confirm-btn">
                    {tfr("Supprimer")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

function EntityCard({ label, avatar, title, subtitle, cta, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-3 text-left rounded-xl border border-border bg-secondary/40 hover:bg-accent px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {avatar}
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className="block text-sm font-semibold text-foreground truncate">{title}</span>
        {subtitle && <span className="block text-[11px] text-muted-foreground truncate">{subtitle}</span>}
      </span>
      <span className="text-[11px] text-athlynx font-medium inline-flex items-center gap-1 shrink-0">
        {cta} <ArrowRight className="h-3 w-3" aria-hidden />
      </span>
    </button>
  );
}

function readableReminder(r) {
  switch (r) {
    case "15m": return tfr("15 minutes avant");
    case "1h": return tfr("1 heure avant");
    case "24h": return tfr("24 heures avant");
    default: return "Aucun rappel";
  }
}
